import styled from '@emotion/styled';
import { colors as dsColors } from '@modernhealth/vital-foundation';
import { useAnalytics } from '@modernhealth/web-analytics';
import React, { useState } from 'react';
import Carousel from 'react-slick';

import iconArrowBack from 'src/public/img/icon-arrow-back.svg';
import iconArrowForward from 'src/public/img/icon-arrow-forward.svg';

import { Benefit } from '../models/benefits';
import { BenefitCard } from './BenefitCard';
import { BenefitModal } from './BenefitModal';

const StyledCarousel = styled(Carousel)({
    '&': {
        marginBottom: 0,
    },
    '& .slick-track': {
        marginLeft: -20,
        display: 'flex',
        padding: 0,

        '&:before, &:after': {
            content: 'none',
        },
    },
    '& button.slick-arrow': {
        '&::before, &::after': {
            color: dsColors.black,
        },
    },
});

const CarouselItem = styled('div')({
    '&&': {
        display: 'flex',
        cursor: 'pointer',
        height: 'auto',
    },
});

interface CarouselArrowImageProps {
    xPosField: string;
    opacity: number;
}

const CarouselArrowImage = styled.img<CarouselArrowImageProps>(({ opacity, xPosField }) => ({
    height: 48,
    width: 48,
    position: 'absolute',
    top: '30%',
    cursor: 'pointer',
    zIndex: 3,
    [xPosField]: -16,
    opacity,
}));

interface CarouselButtonProps {
    prevButton?: boolean;
    onClick?: () => void;
    className?: string;
}

const CarouselButton: React.FC<CarouselButtonProps> = ({ onClick, className = '', prevButton }) => {
    const xPosField = prevButton ? 'left' : 'right';
    const image = prevButton ? iconArrowBack : iconArrowForward;
    const opacity = className.includes('disabled') ? 0 : 1;

    return (
        <CarouselArrowImage xPosField={xPosField} opacity={opacity} src={image} onClick={onClick} />
    );
};

const sliderSettings = {
    infinite: false,
    speed: 500,
    dots: false,
    slidesToShow: 5,
    slidesToScroll: 5,
    nextArrow: <CarouselButton />,
    prevArrow: <CarouselButton prevButton />,
    responsive: [
        {
            breakpoint: 1025,
            settings: {
                slidesToShow: 4,
                slidesToScroll: 4,
                Infinity: true,
            },
        },
        {
            breakpoint: 769,
            settings: {
                slidesToShow: 3,
                slidesToScroll: 3,
                Infinity: true,
            },
        },
        {
            breakpoint: 700,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 2,
                Infinity: true,
            },
        },
        {
            breakpoint: 450,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                Infinity: true,
            },
        },
    ],
};

interface Props {
    featuredBenefits: Benefit[];
}

export const BenefitCardSlider: React.FC<Props> = ({ featuredBenefits = [] }) => {
    const [selectedBenefit, setSelectedBenefit] = useState(undefined);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const analytics = useAnalytics();

    const onBenefitCardSelected = benefit => {
        analytics.trackClick('Individual Featured Benefit Selection', {
            button_copy: benefit.title ?? '',
        });
        setSelectedBenefit(benefit);
        setIsModalVisible(true);
    };

    return (
        <div>
            <StyledCarousel {...sliderSettings}>
                {featuredBenefits.map(benefit => {
                    return (
                        <CarouselItem key={`slide_${benefit.uuid}`}>
                            <BenefitCard
                                benefit={benefit}
                                onClick={benefit => onBenefitCardSelected(benefit)}
                            />
                        </CarouselItem>
                    );
                })}
            </StyledCarousel>
            <BenefitModal
                benefit={selectedBenefit}
                visible={isModalVisible}
                onClose={() => setIsModalVisible(false)}
            />
        </div>
    );
};
